import React from 'react';
import {observer} from 'mobx-react';
import {MatchWidgetProps} from "../models/match-widget.props";
import {CompetitionViewModel} from "../../../domain/common/competition/models/competition-view-model";
import {StandingsWidgetAsync} from "../../standings-widget/components/standings-widget.async";
import {LineupsWidgetAsync} from "../../lineups-widget/components/lineups-widget.async";
import {RelatedGamesModuleAsync} from "../modules/related-games-module/components/related-games-module.async";
import {ScoreboardWidgetAsync} from "../../scoreboard-widget/components/scoreboard-widget.async";
import {MatchEventsModuleAsync} from "../modules/match-events-module/components/match-events-module.async";
import {PredictionsWidgetAsync} from "../../prediction-widget/components/predictions-widget.async";
import {KeyPlayersWidgetAsync} from "../../key-players-widget/components/key-players-widget.async";
import {VideoEmbedded} from "../../../core/components/video-highlight/video-embedded";
import {GameStatsWidgetAsync} from "../../game-stats-widget/components/game-stats-widget.async";
import {TrendsWidgetAsync} from "../../trends-widget/components/trends-widget.async";
import {MatchDescriptionModuleAsync} from "../modules/match-description-module/components/match-description-module.async";
import {CUSTOM_HTML_ENTITY_TYPES, DBA, ENTITY_TYPES} from "../../../core/app/consts";
import {GameShotChartsWidgetAsync} from "../../game-shot-chart-widget/components/game-shot-charts.widget.async";
import styles from '../styles/match-widget.module.scss';
import classNames from "classnames";
import {OddsComparisonWidgetAsync} from "../../odds-comparison-widget/components/odds-comparison-widget.async";
import {H2hWidgetAsync} from "../../h2h-widget/components/h2h-widget.async";
import {PropositionBetsWidgetAsync} from "../../proposition-bets-widget/components/proposition-bets-widget.async";
import {ActualPlayTime} from "../../../core/components/actual-play-time/actual-play-time";
import {OddsByWidgetAsync} from "../../odds-by-widget/components/odds-by-widget.async";
import {NewsWidgetAsync} from "../../news-widget/components/news-widget.async";
import {MATCH_WIDGET} from "../match-widget.consts";
import {IntersectionObserver} from "../../../core/components/intersection-observer/intersection-observer";
import {AD_DISPLAYS} from "../../../core/consts";
// import {MatchFactsWidgetAsync} from "../../match-facts-widget/components/match-facts-widget.async";
import {EXTERNAL_AD_CONFIG} from "../../../apps/website/consts/external-ad-config";
import {ExternalAd} from "../../../core/components/ad/external-ad/external-ad";
import {GameInfoModuleAsync} from "../modules/game-info-module/components/game-info-module.async";
import {BookmakersReviewWidgetAsync} from "../../bookmakers-review-widget/components/bookmakers-review-widget.async";
import classnames from "classnames";
import {NextMatchWidgetAsync} from "../../next-match-widget/components/next-match-widget.async";
import {OddsByPromotionWidgetAsync} from "../../odds-by-promotion-widget/components/odds-by-promotion-widget.async";
import {useCustomHtml} from "../../../infrastructure/hooks/useCustomHtml";
import {CustomHtmlRenderer} from "../../../core/components/custom-html-rerender/custom-html-rerender";
import {RelatedEntitiesWidgetAsync} from "../../related-entities-widget/components/related-entities-widget.async";

export const MatchWidgetEndedGameMobileComponent = observer(({
                                                               controller,
                                                               location,
                                                               onSeeAllClick
                                                             }: MatchWidgetProps) => {
  const {state, device: {isSmallView}} = controller;
  const {
    device,
    actualPlayTime,
    gameStagesCount,
    gameEventsCount,
    shouldRenderGameShotsChart,
    shouldRenderActualPlayTime,
    pageViewModel,
    gridClassName,
    showOdds,
    shouldRenderOddsByPromotions
  } = state

  const {customWidgets} = useCustomHtml(state.language.id, CUSTOM_HTML_ENTITY_TYPES.game, state.gameId);

  const competition: CompetitionViewModel = state.competition;
  const collapsedCompetitorIds: Array<Number> = [state.homeCompetitorId, state.awayCompetitorId];
  const competitionIds: Array<Number> = [competition.id];
  const {items} = pageViewModel;

  const containerProps = {
    className: classNames(styles.element)
  };

  const baseProps = {
    gameId: state.gameId,
    location: location,
  }

  const innerPageProps = {
    ...baseProps,
    onSeeAllClick: (index) => onSeeAllClick && onSeeAllClick(index)
  }

  const intersectionObserverProps = {
    margin: '240px 0px 240px 0px',
  }

  const standingWidgetProps = {
    collapsedCompetitorIds,
    competitionIds,
    stageNum: state.stageNum,
    seasonNum: state.seasonNum,
    shouldUseSuspense: false,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.standings]: isVisible
    })
  };

  // const matchFactsProps = {
  //   ...baseProps,
  //   onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
  //     [MATCH_WIDGET.CONTAINER.KEYS.matchFacts]: isVisible
  //   }),
  //   shouldSupportExpandable: true
  // };

  const videoProps = {
    device,
    title: controller.state.terms.WEB_GAME_OFFICIAL_HIGHLIGHT,
    hasVideo: state.hasVideo,
    video: state.video,
    statusGroup: state.statusGroup,
    gameId: state.gameId,
  }

  const trendsProps = {
    shouldUseSuspense: true,
    isPreview: true,
    entityType: ENTITY_TYPES.game,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.trends]: isVisible
    }),
    ...innerPageProps,
  }

  const oddsComparisonProps = {
    isPreview: true,
    shouldUseSuspense: true,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.oddsComparison]: isVisible
    }),
    ...innerPageProps,
  }

  const propositionBetsProps = {
    isPreview: true,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.props]: isVisible
    }),
    ...innerPageProps,
  }

  const matchEventsProps = {
    ...innerPageProps,
    alwaysShowStages: !state.isScoreboard,
    stagesNum: gameStagesCount,
    eventsNum: gameEventsCount,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.matchEvents]: isVisible
    }),
  }

  const actualPlayTimeProps = {
    controller,
    actualPlayTime,
    onSeeAllClick,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.actualPlayTime]: isVisible
    }),
  }

  const newsProps = {
    gamesIds: [state.gameId],
    shouldRenderSeeAll: true,
    isPreview: true,
    isSmallView: true,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.news]: isVisible
    }),
    ...innerPageProps
  }

  const nextMatchProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.nextMatch]: isVisible
    }),
    ...baseProps
  }

  const relatedGamesProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.relatedGames]: isVisible
    }),
    ...baseProps
  }

  const keyPlayersProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.keyPlayers]: isVisible
    }),
    ...innerPageProps
  }

  const statsProps = {
    isFullView: false,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.stats]: isVisible
    }),
    ...innerPageProps
  }

  const scoreBoardProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.scoreBoard]: isVisible
    }),
    ...innerPageProps
  }

  const shotChartProps = {
    shouldUseSuspense: true,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.shotChart]: isVisible
    }),
    ...innerPageProps
  }

  const lineupsProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.lineups]: isVisible
    }),
    ...innerPageProps
  }

  const gameInfoProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.gameInfo]: isVisible
    }),
    ...baseProps
  }

  const predictionsProps = {
    shouldUseSuspense: true,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.predictions]: isVisible
    }),
    ...baseProps
  }

  const h2hProps = {
    isFullView: false,
    shouldUseSuspense: true,
    matchupId: state.game.matchupId,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.h2h]: isVisible
    }),
    showOdds,
    ...innerPageProps
  }

  const oddsByProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.oddsBy]: isVisible
    }),
    ...innerPageProps
  }

  const oddsByPromotionProps = {
    entityId: state.gameId,
    entityType: ENTITY_TYPES.game,
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.oddsBy]: isVisible
    })
  }

  const bookmakerReviewProps = {
    onWidgetVisible: (isVisible) => pageViewModel.setGridItems({
      [MATCH_WIDGET.CONTAINER.KEYS.bookmakersReview]: isVisible
    }),
    ...innerPageProps
  }

  const relatedEntitiesProps = {
    competitionId: competition?.id,
  }

  const externalAdProps = {
    countryId: state.appState.locale?.userCountryId,
    webPage: DBA.PAGE.match,
    isSmallView,
    langId: state.appState.locale.language.id,
    theme: state.appState.theme,
    isMock: state.appState.settings.config.adDisplay === AD_DISPLAYS.placeholders,
    className: classNames(styles.dba_banner),
    controller
  }

  const mid1AdProps = {
    ...externalAdProps,
    config: EXTERNAL_AD_CONFIG.MOBILE.MATCH.AD320X250_MID1,
  }

  const mid2AdProps = {
    ...externalAdProps,
    config: EXTERNAL_AD_CONFIG.MOBILE.MATCH.AD300X250_MID2,
  }


  return (
    <div className={classNames(styles.grid_container, styles.small_view ,styles.match_ended ,styles[gridClassName])}>
      {
        state.hasVideo && state.video?.isEmbedded && <IntersectionObserver {...intersectionObserverProps}
                                                                           className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.videoEmbedded], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.videoEmbedded)})}>
          <VideoEmbedded {...videoProps}/>
        </IntersectionObserver>
      }

      {
        state.isScoreboard && <IntersectionObserver {...intersectionObserverProps}
                                                    className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.scoreBoard], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.scoreBoard)})}>
          <ScoreboardWidgetAsync {...scoreBoardProps}/>
        </IntersectionObserver>
      }

      {
        state.shouldRenderMatchEvent ? <IntersectionObserver {...intersectionObserverProps}
                                                             className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.matchEvents], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.matchEvents)})}>
          <MatchEventsModuleAsync {...matchEventsProps}/>
        </IntersectionObserver> : null
      }

      {
        state.shouldRenderNextMatch && <IntersectionObserver {...intersectionObserverProps}
                                                             className={classnames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.nextMatch], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.nextMatch)})}>
          <NextMatchWidgetAsync {...nextMatchProps}/>
        </IntersectionObserver>
      }

      {
        state.shouldRenderRelatedGames && <IntersectionObserver {...intersectionObserverProps}
                                                                className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.relatedGames], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.relatedGames)})}>
          <RelatedGamesModuleAsync {...relatedGamesProps}/>
        </IntersectionObserver>
      }

      {
        state.shouldRenderKeyPlayers && <IntersectionObserver {...intersectionObserverProps}
                                                              className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.keyPlayers], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.keyPlayers)})}>
          <KeyPlayersWidgetAsync {...keyPlayersProps}/>
        </IntersectionObserver>
      }

      {
        state.shouldRenderStats ? <IntersectionObserver {...intersectionObserverProps}
                                                        className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.stats], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.stats)})}>
          <GameStatsWidgetAsync {...statsProps}/>
        </IntersectionObserver> : null
      }

      {
        shouldRenderActualPlayTime ? <IntersectionObserver {...intersectionObserverProps}
                                                           className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.actualPlayTime], styles.element, {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.actualPlayTime)})}>
          <ActualPlayTime {...actualPlayTimeProps} isPreview/>
        </IntersectionObserver> : null
      }

      {
        shouldRenderGameShotsChart ? <IntersectionObserver {...intersectionObserverProps}
                                                           className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.shotChart], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.shotChart)})}>
          <GameShotChartsWidgetAsync  {...shotChartProps}/>
        </IntersectionObserver> : null
      }

      {
        state.shouldRenderCurrentPositions && competition?.hasStandings &&
        <IntersectionObserver {...intersectionObserverProps}
                              className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.standings], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.standings)})}>
          <StandingsWidgetAsync {...standingWidgetProps} />
        </IntersectionObserver>
      }

      {
        state.shouldRenderLineups ? <IntersectionObserver {...intersectionObserverProps}
                                                          className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.lineups], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.lineups)})}>
          <LineupsWidgetAsync {...lineupsProps} isCollapsed={true}/>
        </IntersectionObserver> : null
      }

      <IntersectionObserver {...intersectionObserverProps}
                            className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.gameInfo], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.gameInfo)})}>
        <GameInfoModuleAsync {...gameInfoProps}/>
      </IntersectionObserver>

      {
        state.shouldRenderExternalAd ? <div {...containerProps} className={classNames(styles["externalAd_1"])}>
          <ExternalAd {...mid1AdProps}/>
        </div> : null
      }

      <IntersectionObserver {...intersectionObserverProps}
                            className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.predictions], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.predictions)})}>
        <PredictionsWidgetAsync {...predictionsProps}/>
      </IntersectionObserver>

      {
        customWidgets.pl_top ? <IntersectionObserver {...intersectionObserverProps}
                                                            className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.plTop], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.plTop)})}>
          <CustomHtmlRenderer html={customWidgets.pl_top}/>
        </IntersectionObserver> : null
      }

      <IntersectionObserver {...intersectionObserverProps} className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.oddsBy], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.oddsBy)})}>
        {
          shouldRenderOddsByPromotions ? <OddsByPromotionWidgetAsync {...oddsByPromotionProps}/> : <OddsByWidgetAsync {...oddsByProps}/>
        }
      </IntersectionObserver>

      {
        state.shouldRenderTrendsWidget ? <IntersectionObserver {...intersectionObserverProps}
                                                               className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.trends], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.trends)})}>
          <TrendsWidgetAsync {...trendsProps}/>
        </IntersectionObserver> : null
      }

      {
        customWidgets.pl_middle ? <IntersectionObserver {...intersectionObserverProps}
                                                            className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.plMiddle], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.plMiddle)})}>
          <CustomHtmlRenderer html={customWidgets.pl_middle}/>
        </IntersectionObserver> : null
      }

      {
        state.shouldRenderPropositionBets ? <IntersectionObserver {...intersectionObserverProps}
                                                                  className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.props], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.props)})}>
          <PropositionBetsWidgetAsync {...propositionBetsProps}/>
        </IntersectionObserver> : null
      }

      {
        state.shouldRenderH2h ? <IntersectionObserver {...intersectionObserverProps}
                                                      className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.h2h], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.h2h)})}>
          <H2hWidgetAsync {...h2hProps}/>
        </IntersectionObserver> : null
      }

      <IntersectionObserver {...intersectionObserverProps}
                            className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.news], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.news)})}>
        <NewsWidgetAsync {...newsProps}/>
      </IntersectionObserver>

      {
        state.shouldRenderExternalAd ? <div {...containerProps} className={classNames(styles["externalAd_2"])}>
          <ExternalAd {...mid2AdProps}/>
        </div> : null
      }

      {
        state.shouldRenderOddsComparison ? <IntersectionObserver {...intersectionObserverProps}
                                                                 className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.oddsComparison], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.oddsComparison)})}>
          <OddsComparisonWidgetAsync {...oddsComparisonProps}/>
        </IntersectionObserver> : null
      }

      {/*{*/}
      {/*  state.shouldRenderMatchFacts && <IntersectionObserver {...intersectionObserverProps}*/}
      {/*                                                     className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.matchFacts], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.matchFacts)})}>*/}
      {/*    <MatchFactsWidgetAsync {...matchFactsProps} />*/}
      {/*  </IntersectionObserver>*/}
      {/*}*/}

      {
        state.shouldRenderMatchDescriptionModule && <div className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.matchDescription], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.matchDescription)})}>
          <MatchDescriptionModuleAsync gameId={state.gameId} isScheduled={state.game?.isScheduled}/>
        </div>
      }

      {
        !state.isWidget ? <IntersectionObserver className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.bookmakersReview], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.bookmakersReview)})}>
          <BookmakersReviewWidgetAsync {...bookmakerReviewProps}/>
        </IntersectionObserver> : null
      }

      {
        customWidgets.pl_bottom ? <IntersectionObserver {...intersectionObserverProps}
                                                            className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.plBottom], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.plBottom)})}>
          <CustomHtmlRenderer html={customWidgets.pl_bottom}/>
        </IntersectionObserver> : null
      }

      {
        competition?.id ? <IntersectionObserver {...intersectionObserverProps}
                                                  className={classNames(styles.element, styles[MATCH_WIDGET.CONTAINER.KEYS.relatedEntities], {[styles.hide]: !items.get(MATCH_WIDGET.CONTAINER.KEYS.relatedEntities)})}>
          <RelatedEntitiesWidgetAsync {...relatedEntitiesProps}/>
        </IntersectionObserver> : null
      }
    </div>
  );
});
